import React, { useCallback, useEffect, useMemo, useState } from 'react'
import MainBreadcrumb from '../../../../../component-controller-mod/component-serv/navigation-fs-cp/breadcrumb-nav-op/page-main-bcb/ts/main-bcb'
import "../../css/hire.css"
import SelectField from '../../../../../component-controller-mod/component-serv/input-fs-cp/select-field-op/ts/selectField'
import SearchField from '../../../../../component-controller-mod/component-serv/input-fs-cp/search-field-op/ts/searchField'
import { batchDeleteJobItems, columns, deleteJobItem, DepartmentItem, getDepartmentIndexApi, getJobIndexApi, getJobLevelIndexApi, getJobTypeIndexApi, InboxRow, JobIndexItem, toBadgeVariant } from './hire.type'
import { Table, Pagination, Empty, Button, Confirmation } from '@core/uilibrary'
import { DeleteConfirmationIcon, EditIcon, EyeIcon, TrashIcon } from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter'
import StatusBadge from '../../../../../component-controller-mod/component-serv/badge-fs-cp/dynamic-badge-op/ts/statusBadge'
import { useGlobalPopupProvider } from '../../../../../context-controller-mod/context-serv/popup-provider/ts/globalPopupProvider'
import { useToast } from '../../../../../context-controller-mod/context-serv/toast-provider/bottomToast/ts/ToastProvider'
import { useNavigate } from 'react-router-dom'
import { groupPermissionAccessCMSCareer } from '../../../../../context-controller-mod/context-serv/permission-provider/permissionExporter'
import CancelButton from '../../../../../component-controller-mod/component-serv/button-fs-cp/cancel-button-op/ts/cancelButton';

const HireUi = () => {
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rows, setRows] = useState<InboxRow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const { openConfirm, closeConfirm } = useGlobalPopupProvider();
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();
  const [reloadKey, setReloadKey] = useState(0);
  const [departments, setDepartments] = useState<{ value: string; label: string }[]>([
    { value: "all", label: "All" },
  ]);
  const [departmentId, setDepartmentId] = useState<string>("");
  const [levels, setLevels] = useState<{ value: string; label: string }[]>([
    { value: "all", label: "All" },
  ]);
  const [levelId, setLevelId] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const navigate = useNavigate()
  const [jobTypes, setJobTypes] = useState<{ value: string; label: string }[]>([
    { value: "all", label: "All" },
  ]);
  const [jobTypeId, setJobTypeId] = useState<string>("");

  const canCreate = groupPermissionAccessCMSCareer.create()
  const canUpdate = groupPermissionAccessCMSCareer.update()
  const canDelete = groupPermissionAccessCMSCareer.delete()

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        const res = await getJobLevelIndexApi();
        if (!alive) return;

        const items = res?.data?.data?.data?.items ?? [];
        const opts = items
          .filter((x: any) => x.isActive)
          .map((x: any) => ({ value: String(x.id), label: x.name }));

        setLevels([{ value: "all", label: "All" }, ...opts]);
      } catch (e) {
        console.error("GET job level index failed:", e);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        const res = await getJobTypeIndexApi();
        if (!alive) return;

        const items = res?.data?.data?.items ?? [];
        const opts = items
          .filter((x: any) => x.isActive)
          .map((x: any) => ({ value: String(x.id), label: x.name }));

        setJobTypes([{ value: "all", label: "All" }, ...opts]);
      } catch (e) {
        console.error("GET job type index failed:", e);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        const res = await getDepartmentIndexApi();
        if (!alive) return;

        const items: DepartmentItem[] = res?.data?.data?.items ?? [];
        const opts = items
          .filter((d) => d.isActive)
          .map((d) => ({ value: String(d.id), label: d.name }));

        setDepartments([{ value: "all", label: "All" }, ...opts]);
      } catch (e) {
        console.error("GET department index failed:", e);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        setLoading(true);

        const res = await getJobIndexApi(currentPage, rowsPerPage, {
          search: debouncedSearch,
          department: departmentId,
          level: levelId,
          jobType: jobTypeId,
        });

        if (!alive) return;

        const payload = res?.data?.data;
        const items = payload?.items ?? [];
        const pagination = payload?.pagination;

        setTotalItems(pagination?.totalRecords ?? 0);

        const mapped: InboxRow[] = items.map((item: JobIndexItem, idx: number) => {
          const sequentialId = (currentPage - 1) * rowsPerPage + idx + 1;

          return {
            id: item.jobId,
            ID: String(sequentialId),
            Position: item.jobTitle ?? "-",
            "Job level": item.jobLevelName ?? "-",
            Department: item.departmentName ?? "-",
            "Job type": item.jobType ?? "-",
            "Publish date": item.publishedAt
              ? new Date(item.publishedAt).toLocaleDateString()
              : "-",
            Status: item.status ?? "-",
          };
        });

        setRows(mapped);
      } catch (e) {
        console.error("GET career submit failed:", e);
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [currentPage, rowsPerPage, reloadKey, departmentId, levelId, jobTypeId, debouncedSearch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, departmentId, levelId, jobTypeId, debouncedSearch]);


  const handleConfirmDelete = useCallback((rowId?: string) => {
    const idsToDelete =
      selectedIds.size > 0
        ? Array.from(selectedIds)
        : rowId
          ? [rowId]
          : [];

    if (idsToDelete.length === 0) return;

    const count = idsToDelete.length;
    const isBatch = count > 1;

    openConfirm(
      <Confirmation
        visible
        title={
          isBatch
            ? `Delete ${count} selected jobs?`
            : "Delete job?"
        }
        description={
          isBatch
            ? `You are about to permanently delete ${count} selected job${count > 1 ? "s" : ""}. This action cannot be undone.`
            : "You are about to permanently delete this job. This action cannot be undone."
        }
        status="error"
        onClose={closeConfirm}
        icon={<DeleteConfirmationIcon />}
        buttons={
          <>
            <Button
              variant="ghost"
              size="md"
              value="Cancel"
              type="button"
              disabled={saving}
              onClick={closeConfirm}
            />

            <Button
              variant="danger"
              size="md"
              value={
                saving
                  ? "Deleting..."
                  : isBatch
                    ? `Delete ${count} jobs`
                    : "Delete job"
              }
              type="button"
              disabled={saving}
              onClick={async () => {
                try {
                  setSaving(true);

                  const res = isBatch
                    ? await batchDeleteJobItems(idsToDelete)
                    : await deleteJobItem(idsToDelete[0]);

                  const ok =
                    (isBatch && (res?.status === 200 || res?.status === 204)) ||
                    (!isBatch && res?.status === 204);

                  if (ok) {
                    showToast({
                      status: "success",
                      title: "Delete success",
                      description: isBatch
                        ? `${count} jobs deleted successfully.`
                        : "Job deleted successfully.",
                    });
                  } else {
                    showToast({
                      status: "warning",
                      title: "Uncompleted!",
                      description: "No jobs were deleted.",
                    });
                  }

                  closeConfirm();
                  setReloadKey((k) => k + 1);
                  setSelectedIds(new Set());
                } catch (e) {
                  showToast({
                    status: "error",
                    title: "Uncompleted!",
                    description: "Job(s) cannot be deleted.",
                  });
                } finally {
                  setSaving(false);
                }
              }}
            />
          </>
        }
      />
    );
  }, [selectedIds, saving, openConfirm, closeConfirm, showToast]);


  const formattedData = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        Status: <StatusBadge
          variant={toBadgeVariant(row.Status)}
          label={
            row.Status?.toLowerCase() === 'in_review'  ? 'In Review'  :
            row.Status?.toLowerCase() === 'approved'   ? 'Approved'   : //NOSONAR
            row.Status?.toLowerCase() === 'scheduled'  ? 'Scheduled'  : //NOSONAR
            row.Status?.toLowerCase() === 'rejected'   ? 'Rejected'   : //NOSONAR
            row.Status?.toLowerCase() === 'draft'      ? 'Draft'      : //NOSONAR
            '-'
          }
        />,
        Action: (
          <div className="scl--crr-hire-action-const-sty">
            <button
              className="scl--crr-hire-eye-btn-const-sty"
              type="button"
              onClick={() => navigate(`/career/hire/${row.id}/view`)}
            >
              <EyeIcon />
            </button>

            {canUpdate &&
              <button
                className="scl--crr-hire-edit-btn-const-sty"
                type="button"
                onClick={() => navigate(`/career/hire/${row.id}/edit`)}
              >
                <EditIcon />
              </button>
            }

            {canDelete &&
              <>
                <span>|</span>
                <button
                  className="scl--crr-hire-tras-btn-const-sty"
                  type="button"
                  onClick={() => handleConfirmDelete(row.id)}
                >
                  <TrashIcon />
                </button>
              </>
            }

          </div>
        ),
      })),
    [rows, handleConfirmDelete]
  );
  const hasActiveFilters = Boolean(
    (departmentId && departmentId !== 'all') ||
    (jobTypeId && jobTypeId !== 'all') ||
    (levelId && levelId !== 'all') ||
    search
  );

  const handleClearFilters = useCallback(() => {
    setDepartmentId('');
    setJobTypeId('');
    setLevelId('');
    setSearch('');
  }, []);

  return (
    <main className='scl--hire-main-content'>
      <div className="scl--crr-hire-head-const-box">
        <MainBreadcrumb
          title="Job Hiring"
          paragraph="Manage job postings and candidate applications."
          buttonEnabled={canCreate && true}
          buttonLabel="Create"
          onButtonClick={() => navigate(`/career/hire/create`)}
        />
      </div>

      <div className="scl--crr-hire-filt-const-box">
        <div className="scl--crr-hire-filt-deparment">
          <SelectField
            name="department"
            placeholder="Department"
            label="Department"
            options={departments}
            value={departmentId}
            onChange={(val: any) => setDepartmentId(String(val ?? "all"))}
          />
        </div>

        <div className="scl--crr-hire-filt-status">
          <SelectField
            name="jobType"
            placeholder="Job type"
            label="Job type"
            options={jobTypes}
            value={jobTypeId}
            onChange={(val: any) => setJobTypeId(String(val ?? "all"))}
          />
        </div>

        <div className="scl--crr-hire-filt-type">
          <SelectField
            name="level"
            placeholder="Level"
            label="Level"
            options={levels}
            value={levelId}
            onChange={(val: any) => setLevelId(String(val ?? "all"))}
          />
        </div>

        <div className="scl--crr-hire-filt-search">
          <SearchField
            name="search"
            placeholder="Search"
            label="Search"
            value={search}
            onChange={setSearch}
          />
        </div>
        {hasActiveFilters && (
          <CancelButton onClick={handleClearFilters} aria-label="Clear all filters" />
        )}
      </div>

      <div className="scl--crr-hire-tabl-const-box">
        {rows.length === 0 && !loading ? (
          <Empty
            title="No Data yet"
            description="There's nothing here yet. Start by adding new data to see it listed here."
          />
        ) : (
          <>
            <Table
              rowKey="id"
              columns={columns}
              data={formattedData}
              showCheckbox
              showId={false}
              showPagination={false}
              rowsPerPageOptions={[5, 10, 20]}
              defaultRowsPerPage={10}
              backgroundColor="transparent"
              clickableColumns={["Position"]}
              onCellClick={(row: any) => { navigate(`/career/hire/${row.id}/view`) }}
              selectColumnWidth={49}
              onSelectionChange={(selectedRowIds: string[]) => {
                setSelectedIds(new Set(selectedRowIds));
              }}
              columnWidths={{
                ID: "4%",
                Position: "15%",
                "Job level": "15%",
                Department: "15%",
                "Job type": "13%",
                "Publish date": "15%",
                Status: "10%",
                Action: "13%",
              }}
            />

            <Pagination
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              rowsPerPageOptions={[5, 10, 20]}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalItems={totalItems}
            />
          </>
        )}

      </div>
    </main>
  )
}

export default HireUi